import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Bell,
  Check,
  ChevronsUpDown,
  Globe2,
  Plug,
  Save,
  Settings,
  Shield,
  Upload,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/app/components/ui/command';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/app/components/ui/popover';
import { Switch } from '@/app/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { toast } from '@/app/components/ui/simple-toaster';
import {
  useOrganizationSettings,
  usePreviewMfaRequirement,
  useUpdateOrganizationSettings,
} from '@/hooks/use-settings';
import { useOnboardingState, useUpdateOnboardingSteps } from '@/hooks/use-onboarding';
import { Skeleton } from '@/app/components/ui/skeleton';
import { usePermissions } from '@/hooks/use-permissions';
import { usePlatformModules } from '@/hooks/use-modules';
import { BrandingCard } from './branding-card';
import type { BrandingCardRef } from './branding-card';
import { PaymentsCard } from './payments-card';
import { EmailIdentityCard } from './email-identity-card';
import { DepartmentsSettingsCard } from './departments-settings-card';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import { CURRENCIES } from '@/lib/currency';
import { cn } from '@/app/components/ui/utils';
import type { MoneyRecordCounts, UserRole } from '@/lib/types';

const MFA_ROLES: UserRole[] = ['Admin', 'Manager', 'Inspector', 'Supervisor', 'User', 'Viewer'];
const TIME_ZONES = ['UTC', ...Intl.supportedValuesOf('timeZone')];
const TIME_ZONE_CHANGE_WARNING = 'Previously generated PDFs will re-render in the new zone. Stored times are not changed.';
const CURRENCY_CHANGE_WARNING = 'Changing the currency relabels all existing amounts. No conversion is applied. Set this before you create bids, invoices or payroll.';

const MONEY_RECORD_COUNT_LABELS: Record<keyof MoneyRecordCounts, string> = {
  bids: 'Bids',
  invoices: 'Invoices',
  invoice_payments: 'Invoice payments',
  change_orders: 'Change orders',
  projects_with_budget_or_rate: 'Projects with budget or rate',
  payroll_runs: 'Payroll runs',
  rate_records: 'Rate records',
};

interface TimeZoneSettingProps {
  value: string;
  onChange?: (timeZone: string) => void;
  readOnly?: boolean;
}

function TimeZoneSetting({ value, onChange, readOnly = false }: TimeZoneSettingProps) {
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe2 className="h-5 w-5" />
          Time zone
        </CardTitle>
        <CardDescription>
          Used for organization-local dates, times, and scheduled activity
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <Label htmlFor="organization-time-zone">Time zone</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              id="organization-time-zone"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full max-w-md justify-between font-normal"
              disabled={readOnly}
            >
              {value}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
            <Command>
              <CommandInput placeholder="Search time zones..." />
              <CommandList>
                <CommandEmpty>No time zone found.</CommandEmpty>
                {TIME_ZONES.map((timeZone) => (
                  <CommandItem
                    key={timeZone}
                    value={timeZone}
                    onSelect={() => {
                      onChange?.(timeZone);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === timeZone ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {timeZone}
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <p className="text-sm text-gray-500 dark:text-muted-foreground">
          Current date and time in this zone:{' '}
          {formatDateTimeInTimeZone(new Date(now).toISOString(), value)}
        </p>
        {readOnly && (
          <p className="text-sm text-gray-500 dark:text-muted-foreground">
            Only organization administrators can change this setting.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

interface CurrencySettingProps {
  value: string;
  onChange?: (currency: string) => void;
  readOnly?: boolean;
}

function CurrencySetting({ value, onChange, readOnly = false }: CurrencySettingProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe2 className="h-5 w-5" />
          Currency
        </CardTitle>
        <CardDescription>
          Used to label organization-level project, invoice, and payroll amounts
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <Label htmlFor="organization-currency">Currency</Label>
        <Select value={value} onValueChange={onChange} disabled={readOnly}>
          <SelectTrigger id="organization-currency" className="w-full max-w-md">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CURRENCIES.map(({ code, name }) => (
              <SelectItem key={code} value={code}>
                {code} — {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-sm text-gray-500 dark:text-muted-foreground">
          {CURRENCY_CHANGE_WARNING}
        </p>
        {readOnly && (
          <p className="text-sm text-gray-500 dark:text-muted-foreground">
            Only organization administrators can change this setting.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export function SettingsPage() {
  const { canManageOrganization } = usePermissions();
  const { data: settings, isLoading, error } = useOrganizationSettings({
    requireFreshMoneyRecordCounts: true,
  });
  const updateSettings = useUpdateOrganizationSettings();
  const { mutateAsync: previewMfaRequirement } = usePreviewMfaRequirement();
  const updateOnboardingSteps = useUpdateOnboardingSteps();
  const { data: onboardingState } = useOnboardingState();
  const navigate = useNavigate();
  const { isModuleEnabled } = usePlatformModules();
  const hasGearModule = isModuleEnabled('inventory');
  const hasSafetyModule = isModuleEnabled('safety');
  const hasCrewModule = isModuleEnabled('crew');

  const [inspectionInterval, setInspectionInterval] = useState('');
  const [deploymentMaxDays, setDeploymentMaxDays] = useState('');
  const [requireInspection, setRequireInspection] = useState(false);
  const [requireRescuePlan, setRequireRescuePlan] = useState(true);
  const [emergencyServicesNumber, setEmergencyServicesNumber] = useState('');
  const [emergencyServicesError, setEmergencyServicesError] = useState('');
  const [icMinRole, setIcMinRole] = useState<'manager' | 'supervisor'>('manager');
  const [requiredMfaRoles, setRequiredMfaRoles] = useState<UserRole[]>([]);
  const [mfaPreview, setMfaPreview] = useState<{
    roles: string;
    count: number;
  } | null>(null);
  const [timeZone, setTimeZone] = useState('UTC');
  const [currency, setCurrency] = useState('USD');
  const [certificationExpiryRemindersEnabled, setCertificationExpiryRemindersEnabled] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [brandingDirty, setBrandingDirty] = useState(false);
  const [isSavingBranding, setIsSavingBranding] = useState(false);
  const [showTimeZoneConfirmation, setShowTimeZoneConfirmation] = useState(false);
  const [showCurrencyConfirmation, setShowCurrencyConfirmation] = useState(false);
  const brandingRef = useRef<BrandingCardRef | null>(null);

  // Track whether the user has made local edits so that a query refetch
  // triggered by another card's save doesn't silently discard our changes.
  const dirtyRef = useRef(false);
  const requiredMfaRolesKey = JSON.stringify(requiredMfaRoles);
  const mfaRolesChanged = Boolean(
    settings &&
      requiredMfaRolesKey !== JSON.stringify(settings.required_mfa_roles ?? []),
  );
  const mfaPreviewCount =
    mfaRolesChanged && mfaPreview?.roles === requiredMfaRolesKey
      ? mfaPreview.count
      : null;

  useEffect(() => {
    if (settings && !dirtyRef.current) {
      setInspectionInterval(settings.inspection_default_interval_days?.toString() || '');
      setDeploymentMaxDays(settings.deployment_max_days?.toString() || '');
      setRequireInspection(settings.require_inspection_before_deploy || false);
      setRequireRescuePlan(settings.require_signed_rescue_plan_for_jsa ?? true);
      setEmergencyServicesNumber(settings.emergency_services_number);
      setIcMinRole(settings.inspection_certified_min_role || 'manager');
      setRequiredMfaRoles(settings.required_mfa_roles ?? []);
      setTimeZone(settings.time_zone);
      setCurrency(settings.currency);
      setCertificationExpiryRemindersEnabled(settings.certification_expiry_reminders_enabled);
    }
  }, [settings]);

  useEffect(() => {
    if (settings) {
      const intervalChanged = inspectionInterval !== (settings.inspection_default_interval_days?.toString() || '');
      const maxDaysChanged = deploymentMaxDays !== (settings.deployment_max_days?.toString() || '');
      const requireChanged = requireInspection !== (settings.require_inspection_before_deploy || false);
      const rescuePlanChanged = requireRescuePlan !== (settings.require_signed_rescue_plan_for_jsa ?? true);
      const emergencyServicesChanged = emergencyServicesNumber !== settings.emergency_services_number;
      const icChanged = icMinRole !== (settings.inspection_certified_min_role || 'manager');
      const mfaChanged = JSON.stringify(requiredMfaRoles) !== JSON.stringify(settings.required_mfa_roles ?? []);
      const timeZoneChanged = timeZone !== settings.time_zone;
      const currencyChanged = currency !== settings.currency;
      const certRemindersChanged = certificationExpiryRemindersEnabled !== settings.certification_expiry_reminders_enabled;
      const dirty = intervalChanged || maxDaysChanged || requireChanged || rescuePlanChanged || emergencyServicesChanged || icChanged || mfaChanged || timeZoneChanged || currencyChanged || certRemindersChanged || brandingDirty;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasChanges(dirty);
      dirtyRef.current = dirty;
    }
  }, [inspectionInterval, deploymentMaxDays, requireInspection, requireRescuePlan, emergencyServicesNumber, icMinRole, requiredMfaRoles, timeZone, currency, certificationExpiryRemindersEnabled, settings, brandingDirty]);

  useEffect(() => {
    if (!settings) return;
    if (!mfaRolesChanged) return;
    let active = true;
    previewMfaRequirement(requiredMfaRoles)
      .then((response) => {
        if (active) {
          setMfaPreview({
            roles: requiredMfaRolesKey,
            count: response.newly_required_count,
          });
        }
      })
      .catch(() => setMfaPreview(null));
    return () => {
      active = false;
    };
  }, [mfaRolesChanged, requiredMfaRoles, requiredMfaRolesKey, settings, previewMfaRequirement]);

  const handleSave = async () => {
    const normalizedEmergencyServicesNumber = emergencyServicesNumber.trim();
    setEmergencyServicesError(
      normalizedEmergencyServicesNumber
        ? ''
        : 'Confirm the emergency services number for this organization.'
    );
    const brandingSaveRequested = brandingDirty;

    try {
      if (brandingSaveRequested && brandingRef.current) {
        setIsSavingBranding(true);
        try {
          await brandingRef.current.save();
        } catch {
          // Branding card already shows its own specific toast; don't proceed
          // and don't show the generic settings save error.
          return;
        } finally {
          setIsSavingBranding(false);
        }
        // Mark the "Complete company profile" onboarding step as done as soon
        // as the company profile/branding portion has persisted.
        updateOnboardingSteps.mutate({ steps: { company_profile: true } });
      }

      await updateSettings.mutateAsync({
        inspection_default_interval_days: parseInt(inspectionInterval, 10) || 0,
        deployment_max_days: parseInt(deploymentMaxDays, 10) || 0,
        require_inspection_before_deploy: requireInspection,
        require_signed_rescue_plan_for_jsa: requireRescuePlan,
        ...(normalizedEmergencyServicesNumber !== settings?.emergency_services_number
          ? { emergency_services_number: normalizedEmergencyServicesNumber }
          : {}),
        inspection_certified_min_role: icMinRole,
        required_mfa_roles: requiredMfaRoles,
        time_zone: timeZone,
        currency,
        certification_expiry_reminders_enabled: certificationExpiryRemindersEnabled,
      });
      toast.success('Settings Saved', {
        description: 'Organization settings have been updated successfully.',
      });
      dirtyRef.current = false;

      // Redirect back to onboarding wizard only when company profile/branding
      // was part of the save and onboarding is still in progress.
      if (brandingSaveRequested && onboardingState && !onboardingState.onboarding_completed) {
        navigate('/onboarding');
      }
    } catch (_err) {
      if (brandingSaveRequested) {
        toast.error('Error', {
          description: 'Branding saved, but settings failed to save. Please try again.',
        });
      } else {
        toast.error('Error', {
          description: 'Failed to save settings. Please try again.',
        });
      }
    }
  };

  const handleReset = () => {
    if (settings) {
      dirtyRef.current = false;
      setInspectionInterval(settings.inspection_default_interval_days?.toString() || '');
      setDeploymentMaxDays(settings.deployment_max_days?.toString() || '');
      setRequireInspection(settings.require_inspection_before_deploy || false);
      setRequireRescuePlan(settings.require_signed_rescue_plan_for_jsa ?? true);
      setEmergencyServicesNumber(settings.emergency_services_number);
      setEmergencyServicesError('');
      setIcMinRole(settings.inspection_certified_min_role || 'manager');
      setRequiredMfaRoles(settings.required_mfa_roles ?? []);
      setTimeZone(settings.time_zone);
      setCurrency(settings.currency);
      setCertificationExpiryRemindersEnabled(settings.certification_expiry_reminders_enabled);
    }
    brandingRef.current?.reset();
  };

  const timeZoneChanged = settings != null && timeZone !== settings.time_zone;
  const currencyChanged = settings != null && currency !== settings.currency;
  const currencyNeedsConfirmation = currencyChanged && Boolean(settings?.has_org_currency_records);
  const requestSave = () => {
    if (currencyNeedsConfirmation) {
      setShowCurrencyConfirmation(true);
      return;
    }
    if (timeZoneChanged) {
      setShowTimeZoneConfirmation(true);
      return;
    }
    void handleSave();
  };
  const confirmCurrencyAndContinue = () => {
    if (timeZoneChanged) {
      setShowTimeZoneConfirmation(true);
      return;
    }
    void handleSave();
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-1/4" />
        <Skeleton className="h-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Error Loading Settings</h3>
                <p className="text-sm text-red-800 mt-1">
                  Failed to load organization settings. Please try refreshing the page.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!canManageOrganization) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-foreground">Organization Settings</h1>
          <p className="text-gray-600 dark:text-muted-foreground mt-1">View system-wide preferences for your organization</p>
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <TimeZoneSetting value={settings?.time_zone ?? 'UTC'} readOnly />
          <CurrencySetting value={settings?.currency ?? 'USD'} readOnly />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-foreground">Organization Settings</h1>
          <p className="text-gray-600 dark:text-muted-foreground mt-1">Configure system-wide preferences for your organization</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <Link to="/onboarding">Open setup wizard</Link>
          </Button>
          {hasChanges && (
            <Button variant="outline" onClick={handleReset}>
              Reset
            </Button>
          )}
          <Button onClick={requestSave} disabled={!hasChanges || updateSettings.isPending || isSavingBranding}>
            <Save className="h-4 w-4 mr-2" />
            {updateSettings.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <TimeZoneSetting value={timeZone} onChange={setTimeZone} />
        <CurrencySetting value={currency} onChange={setCurrency} />
        {hasCrewModule && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Certification expiry reminders
              </CardTitle>
              <CardDescription>
                Email technicians and organization admins/managers before certifications expire
              </CardDescription>
            </CardHeader>
            <CardContent className="flex items-center justify-between gap-4">
              <div>
                <Label htmlFor="certification-expiry-reminders">
                  Send reminders at 60, 30, and 7 days
                </Label>
                <p className="text-sm text-gray-500 dark:text-muted-foreground">
                  Enabled by default for every organization using H3Crew.
                </p>
              </div>
              <Switch
                id="certification-expiry-reminders"
                checked={certificationExpiryRemindersEnabled}
                onCheckedChange={setCertificationExpiryRemindersEnabled}
              />
            </CardContent>
          </Card>
        )}
      </div>
      <DepartmentsSettingsCard />
      {timeZoneChanged && (
        <div className="flex max-w-2xl items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{TIME_ZONE_CHANGE_WARNING}</p>
        </div>
      )}

      <AlertDialog
        open={showTimeZoneConfirmation}
        onOpenChange={setShowTimeZoneConfirmation}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm time-zone change</AlertDialogTitle>
            <AlertDialogDescription>
              {TIME_ZONE_CHANGE_WARNING}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleSave()}>
              Confirm and save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showCurrencyConfirmation}
        onOpenChange={setShowCurrencyConfirmation}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm currency change</AlertDialogTitle>
            <AlertDialogDescription>
              {CURRENCY_CHANGE_WARNING}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {settings && (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {Object.entries(settings.money_record_counts).map(([key, count]) => (
                <div key={key} className="flex justify-between gap-3">
                  <dt>{MONEY_RECORD_COUNT_LABELS[key as keyof MoneyRecordCounts]}</dt>
                  <dd className="font-medium">{count}</dd>
                </div>
              ))}
            </dl>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCurrency(settings?.currency ?? 'USD')}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmCurrencyAndContinue}>
              Confirm and save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BrandingCard ref={brandingRef} onDirtyChange={setBrandingDirty} />

      <PaymentsCard />

      <EmailIdentityCard />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Required Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Require app-login TOTP enrollment separately for each organization role
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {mfaPreviewCount !== null && (
            <p className="text-sm text-muted-foreground sm:col-span-2" data-testid="mfa-requirement-preview">
              {mfaPreviewCount === 0
                ? 'No users will newly need to set up 2FA.'
                : `${mfaPreviewCount} ${mfaPreviewCount === 1 ? 'user' : 'users'} will newly need to set up 2FA.`}
            </p>
          )}
          {MFA_ROLES.map((role) => (
            <div key={role} className="flex items-center justify-between rounded-lg border p-3">
              <Label htmlFor={`required-mfa-${role}`} className="cursor-pointer">
                {role}
              </Label>
              <Switch
                id={`required-mfa-${role}`}
                checked={requiredMfaRoles.includes(role)}
                onCheckedChange={(checked) => {
                  setRequiredMfaRoles((current) => (
                    checked
                      ? MFA_ROLES.filter((item) => item === role || current.includes(item))
                      : current.filter((item) => item !== role)
                  ));
                }}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Data Import
          </CardTitle>
          <CardDescription>
            Migrate pilot data from spreadsheets — techs, certifications, clients, and projects
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" asChild>
            <Link to="/settings/data-import">Import data</Link>
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plug className="h-5 w-5" />
            Integrations
          </CardTitle>
          <CardDescription>
            Connect QuickBooks, Xero and other external services
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" asChild>
            <Link to="/settings/integrations">Manage integrations</Link>
          </Button>
        </CardContent>
      </Card>

      {hasGearModule && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Inspection Settings
          </CardTitle>
          <CardDescription>
            Configure default inspection intervals and requirements
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="inspection-interval">Default Inspection Interval (days)</Label>
            <Input
              id="inspection-interval"
              type="number"
              min="1"
              max="365"
              placeholder="e.g., 90"
              value={inspectionInterval}
              onChange={(e) => setInspectionInterval(e.target.value)}
              className="max-w-xs"
            />
            <p className="text-sm text-gray-500 dark:text-muted-foreground">
              The default number of days between required inspections for new assets
            </p>
          </div>

          <div className="flex items-center justify-between max-w-md">
            <div className="space-y-0.5">
              <Label htmlFor="require-inspection">Require Inspection Before Deployment</Label>
              <p className="text-sm text-gray-500 dark:text-muted-foreground">
                Assets must have a recent inspection before being checked out
              </p>
            </div>
            <Switch
              id="require-inspection"
              checked={requireInspection}
              onCheckedChange={setRequireInspection}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ic-min-role">Who can toggle Inspection Certified?</Label>
            <Select value={icMinRole} onValueChange={(v) => setIcMinRole(v as 'manager' | 'supervisor')}>
              <SelectTrigger id="ic-min-role" className="max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manager">Manager and above</SelectItem>
                <SelectItem value="supervisor">Supervisor and above</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-500 dark:text-muted-foreground">
              Controls the minimum role required to toggle the Inspection Certified flag on employee profiles
            </p>
          </div>
        </CardContent>
      </Card>
      )}

      {hasGearModule && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Deployment Settings
          </CardTitle>
          <CardDescription>
            Configure deployment duration limits and rules
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="deployment-max-days">Maximum Deployment Duration (days)</Label>
            <Input
              id="deployment-max-days"
              type="number"
              min="1"
              max="365"
              placeholder="e.g., 30"
              value={deploymentMaxDays}
              onChange={(e) => setDeploymentMaxDays(e.target.value)}
              className="max-w-xs"
            />
            <p className="text-sm text-gray-500 dark:text-muted-foreground">
              The maximum number of days an asset can be deployed before requiring return
            </p>
          </div>
        </CardContent>
      </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Emergency Settings
          </CardTitle>
          <CardDescription>
            Configure organization-wide defaults for emergency planning
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="emergency-services-number">Emergency services number</Label>
            <Input
              id="emergency-services-number"
              value={emergencyServicesNumber}
              onChange={(e) => {
                setEmergencyServicesNumber(e.target.value);
                if (e.target.value.trim()) {
                  setEmergencyServicesError('');
                }
              }}
              className="max-w-xs"
              aria-invalid={Boolean(emergencyServicesError)}
              aria-describedby={emergencyServicesError ? 'emergency-services-number-error' : undefined}
            />
            {emergencyServicesError && (
              <p id="emergency-services-number-error" className="text-sm text-destructive">
                {emergencyServicesError}
              </p>
            )}
            <p className="text-sm text-gray-500 dark:text-muted-foreground">
              Pre-filled on new JSAs and rescue plans; users can edit it on each record
            </p>
          </div>
        </CardContent>
      </Card>

      {hasSafetyModule && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            H3Safe Settings
          </CardTitle>
          <CardDescription>
            Configure rescue plan and JSA submission rules
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between max-w-md">
            <div className="space-y-0.5">
              <Label htmlFor="require-rescue-plan">Require signed rescue plan before JSA submission</Label>
              <p className="text-sm text-gray-500 dark:text-muted-foreground">
                When off, admins can submit JSAs without a linked signed rescue plan by providing a mandatory reason
              </p>
            </div>
            <Switch
              id="require-rescue-plan"
              checked={requireRescuePlan}
              onCheckedChange={setRequireRescuePlan}
            />
          </div>
        </CardContent>
      </Card>
      )}

      {settings && (
        <Card className="border-gray-200 dark:border-border bg-gray-50 dark:bg-secondary">
          <CardContent className="pt-6">
            <div className="text-sm text-gray-600 dark:text-muted-foreground">
              <p>
                Last updated:{' '}
                {formatDateTimeInTimeZone(settings.updated_at, settings.time_zone)}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
